import moment from 'moment'
import { Body, Iterable, Params } from '@/utils/interfaces'
import { axiosApi } from '@/axios'
import { AxiosError, AxiosResponse } from 'axios'
import { currencySymbols } from './currencySymbols'
import store from '@/store'
import { User } from '@/api/Auth/auth.interface'
import LogRocket from 'logrocket'
import {
  getAgencyPrices,
  getProviders,
  getSharedProducts,
  getSharedProviders,
} from '@/api/Topup/topup.api'
import { message } from '@/store/modules/notifications'
import { TranslateResult } from 'vue-i18n'
import * as Sentry from '@sentry/browser'
import i18n from '@/i18n'

export function fromObjectToFormData(item: object) {
  const formdata = new FormData()
  Object.entries(item).forEach(([key, value]: [string, string | object]) => {
    if (typeof value === 'object' && !(value instanceof File)) {
      Object.entries(value).forEach(
        ([nestedKey, nestedValue]: [string, string]) => {
          formdata.append(`${key}[${nestedKey}]`, nestedValue)
        },
      )
    } else formdata.append(key, value)
  })
  return formdata
}

function postOrPatch<T>(url: string, body: Body) {
  if (body.uuid) {
    return axiosApi.patch<T>(`${url}${body.uuid}/`, body)
  } else return axiosApi.post<T>(url, body)
}

export async function createOrUpdate<T>(
  url: string,
  body: Body,
  fileField?: string,
): Promise<AxiosResponse> {
  if (!fileField) {
    return postOrPatch<T>(url, body)
  } else if (typeof body[fileField] !== 'string' && body[fileField]) {
    const formdata = new FormData()
    formdata.append(fileField, body[fileField] as string)
    const newBody = { ...body }
    delete newBody[fileField]
    const response = await postOrPatch<T>(url, newBody)
    // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
    // @ts-ignore
    return axiosApi.patch<T>(`${url}${response.data.uuid}/`, formdata, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  } else {
    const newBody = { ...body }
    delete newBody[fileField]
    return postOrPatch(url, newBody)
  }
}

export function getSymbolFromCurrency(currencyCode: string) {
  if (typeof currencyCode !== 'string') return undefined
  const code = currencyCode.toUpperCase()
  if (!currencySymbols.code) return undefined
  return currencySymbols[code]
}

export function createOrUpdateJSON<T>(
  url: string,
  body: any,
): Promise<AxiosResponse<T>> {
  if (body.uuid) {
    return axiosApi.patch<T>(url + body.uuid + '/', body)
  } else return axiosApi.post<T>(url, body)
}

export const formatDate = (
  date: string,
  format = 'ddd, MMM DD YYYY, hh:mm a',
) => {
  moment.locale(store.getters.profile.lang)
  return moment(date).format(format)
}

export const formatNumber = (
  value: number | string,
  minimumFractionDigits = 2,
) =>
  Number(value).toLocaleString('en-US', {
    minimumFractionDigits,
  })

export const formatCurrency = (
  value: number | string,
  fractionDigits = 2,
  currency = 'USD',
) => {
  if (!value) value = 0
  const number = Number(value)
  return Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: fractionDigits,
  }).format(number)
}

export const formatLongCurrency = (value: number | string) => {
  return formatCurrency(value, 4)
}

export const identifySentry = () => {
  Sentry.setUser({
    email: store.state.profile?.email,
    id: store.state.profile?.uuid,
    username: store.state.profile?.username,
    role: store.state.profile?.role,
  })
}

export const identifyLogrocket = (user: User) => {
  if (
    process.env.VUE_APP_BRANCH &&
    process.env.VUE_APP_BRANCH.toLowerCase() === 'master'
  ) {
    LogRocket.identify(user.uuid, {
      username: user.username,
      name: user.first_name + ' ' + user.last_name,
      email: user.email,
    })
    LogRocket.init(process.env.VUE_APP_LOGROCKET_DSN as string)

    LogRocket.getSessionURL(sessionURL => {
      Sentry.configureScope(scope => {
        scope.setExtra('sessionURL', sessionURL)
      })
    })
  }
}

export async function urltoFile(
  url: string,
  filename = 'file',
  mimeType = 'image/webp',
) {
  const res = await fetch(url)
  const buf = await res.arrayBuffer()
  return new File([buf], filename, { type: mimeType })
}

export async function getAgencyProviders(params?: Params) {
  const responseProviders = await getProviders({
    agency__uuid: store.getters.agency.uuid,
    ...params,
    limit: 500,
  })
  const responseSharedProviders = await getSharedProviders({
    agency__uuid: store.getters.agency.uuid,
    ...params,
    limit: 500,
  })

  const providers = responseProviders
  providers.data.count += responseSharedProviders.data.count
  providers.data.results = [
    ...providers.data.results,
    ...responseSharedProviders.data.results,
  ]
  return providers
}

export async function getAgencySharedProviders(params?: Params) {
  const responseProviders = await getSharedProviders({
    ...params,
    limit: 500,
  })
  const providers = responseProviders
  providers.data.results = providers.data.results.filter(
    item => item.agency !== store.getters.agency.uuid,
  )
  return providers
}

export async function getAgencyProducts(params?: Params) {
  const agencyPrices = await getAgencyPrices({
    ...params,
    agency__uuid: store.getters.agency.uuid,
    limit: 1000,
  })
  const agencyShared = await getSharedProducts({
    ...params,
    agency__uuid: store.getters.agency.uuid,
    limit: 1000,
  })

  agencyPrices.data.count += agencyShared.data.count
  agencyPrices.data.results = [
    ...agencyPrices.data.results,
    ...agencyShared.data.results,
  ]
  return agencyPrices
}

export async function getAgencySharedProducts(params?: Params) {
  const response = await getSharedProducts({ ...params, limit: 1000 })
  response.data.results = response.data.results.filter(
    item => item.agency !== store.getters.agency.uuid,
  )
  return response
}

export function getKeyAndValue(object: object): [string, string] {
  const [key, value] = Object.entries(object)[0]
  if (value instanceof Array) {
    if (typeof value[0] === 'string') {
      return [key, value[0]]
    } else return getKeyAndValue(value[0])
  } else if (typeof value === 'object') {
    return getKeyAndValue(value)
  } else return [key, value]
}

export function displayInSnack(error: AxiosError) {
  if (error.response) {
    let description: string | TranslateResult
    let title: string
    let text: string | TranslateResult
    if (error.response.status === 500) {
      description = i18n.t('system_error')
      title = 'Error'
      text = i18n.t('system_error')
    } else if (error.response.status === 404) {
      description = i18n.t('resource_not_found')
      title = 'Error'
      text = i18n.t('resource_not_found')
    } else {
      ;[title, description] = getKeyAndValue(error.response.data)
      text = 'Request failed with status ' + error.response?.status
    }
    const payload: message = {
      color: 'error',
      description: description,
      icon: 'mdi-alert',
      title: title,
      text: text,
    }
    store.commit('notifications/displayMessage', payload)
  } else if (error.request) {
    const err = error.toJSON() as AxiosError
    const payload: message = {
      color: 'error',
      description: err.message,
      icon: 'mdi-alert',
      title: error.name,
      text: err.message,
    }
    store.commit('notifications/displayMessage', payload)
  }
}

export function copyToClipboard(text: string) {
  const listener = function(ev: ClipboardEvent) {
    ev.preventDefault()
    if (ev.clipboardData) {
      ev.clipboardData.setData('text/plain', text)
    }
  }
  document.addEventListener('copy', listener)
  document.execCommand('copy')
  document.removeEventListener('copy', listener)
}

export function getText(
  item: Iterable,
  fields: { text: TranslateResult; field: string }[],
) {
  let string = ''
  fields.forEach(field => {
    if (item[field.field]) {
      string += `${field.text}: ${item[field.field]}\n`
    }
  })
  return string
}

export function compareJSON(original?: Iterable, copy?: Iterable): string[] {
  if (!original || !copy) return []
  const entries = Object.entries(original)
  let differentFields: string[] = []
  entries.forEach(([key, value]) => {
    if (typeof value === 'string' && value !== copy[key]) {
      differentFields.push(key)
    } else if (typeof value === 'object') {
      differentFields = [...differentFields, ...compareJSON(value, copy[key])]
    }
  })
  return differentFields
}

export function getCardInfo(value: string) {
  if (!value) return ''
  const card = value.split(' ').join('')

  if (
    card.startsWith('922595987') ||
    card.startsWith('922695987') ||
    card.startsWith('922895987') ||
    card.startsWith('923595987')
  )
    return 'METROPOLITANO'
  if (
    card.startsWith('922512997') ||
    card.startsWith('924712997') ||
    card.startsWith('924512997') ||
    card.startsWith('922612997')
  )
    return 'BPA'
  if (card.startsWith('923512997') || card.startsWith('924512997')) return 'BPA'
  if (
    card.startsWith('922506999') ||
    card.startsWith('923506999') ||
    card.startsWith('922606999')
  )
    return 'BANDEC'
  if (card.startsWith('9560')) return 'AIS'
  if (card.startsWith('924006999')) return 'BANDEC USD Prepago'
  if (card.startsWith('97600390')) return 'Clásica'
  return ''
}

export function getAccountNumberInfo(value: string) {
  if (!value) return ''
  const card = value.split(' ').join('')

  if (card.startsWith('05987')) return 'METROPOLITANO'
  if (card.startsWith('12997')) return 'BPA'
  if (card.startsWith('06999')) return 'BANDEC'
  return ''
}

export async function generateFile(response: AxiosResponse, filename: string) {
  const link = document.createElement('a')
  const blob = new Blob([response.data], {
    type: response.headers['Content-Type'],
  })
  link.href = URL.createObjectURL(blob)
  link.target = '_blank'
  link.download = filename
  link.click()
  link.remove()
}
